import dompurify from 'dompurify';

// contentId (without angle brackets) → short-lived URL of the stored inline attachment
export type InlineImageSources = Record<string, string>;

const CID_PREFIX = 'cid:';
const PLACEHOLDER_STYLE =
  'display:inline-block;padding:2px 8px;border:1px dashed #9ca3af;border-radius:4px;color:#6b7280;font-size:12px';
// Images may load from anywhere; style sheets, fonts and everything else stay inline
const CONTENT_SECURITY_POLICY =
  "default-src 'none'; img-src * data: blob:; style-src 'unsafe-inline'; font-src data:";

// A whole document for a sandboxed iframe, the mail's own style sheets kept; '' when nothing shows
export const renderEmailHtml = (
  bodyHtml: string,
  inlineSources: InlineImageSources,
  placeholderText: string,
): string => {
  const parsed = new DOMParser().parseFromString(bodyHtml, 'text/html');

  parsed.querySelectorAll('img').forEach(image => {
    const source = image.getAttribute('src') ?? '';
    if (!source.startsWith(CID_PREFIX)) return;
    const url = inlineSources[source.slice(CID_PREFIX.length)];
    if (url) {
      image.setAttribute('src', url);
      return;
    }
    const placeholder = parsed.createElement('span');
    placeholder.setAttribute('style', PLACEHOLDER_STYLE);
    placeholder.setAttribute('data-inline-placeholder', '');
    placeholder.textContent = placeholderText;
    image.replaceWith(placeholder);
  });

  parsed.querySelectorAll('a[href]').forEach(anchor => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  const sanitized = dompurify.sanitize(parsed.documentElement.outerHTML, {
    ADD_ATTR: ['target'],
    WHOLE_DOCUMENT: true,
    RETURN_DOM: true,
  });
  if (!(sanitized instanceof HTMLElement) || !sanitized.querySelector('body')?.innerHTML) return '';
  const policy = sanitized.ownerDocument.createElement('meta');
  policy.setAttribute('http-equiv', 'Content-Security-Policy');
  policy.setAttribute('content', CONTENT_SECURITY_POLICY);
  sanitized.querySelector('head')?.prepend(policy);
  // Standards mode: in quirks mode the root stretches to the frame and can't be measured
  return `<!DOCTYPE html>${sanitized.outerHTML}`;
};
